/**
 * editor-styles.js — the look of the edited document, owned by the BUNDLE.
 *
 * Until now docs/editor.md asked every host app to paste a block of prose CSS
 * into its own stylesheet, or the editor rendered as unstyled runs of text.
 * That was survivable while the editor only knew headings, lists and quotes.
 * It stops being survivable the moment tables exist: prosemirror-tables NEEDS
 * `table-layout: fixed` for column resizing, and a table with no cell borders
 * is not a table the user can see, let alone edit. Shipping a component whose
 * appearance lives in 500 app stylesheets is not shippable via a CDN — which
 * is the whole point of shipping it via a CDN. Same reasoning as ensureDragStyles().
 *
 * EVERY selector is wrapped in :where(), which zeroes its specificity. So the
 * `.g-editor-root h1 { ... }` rules the docs told apps to write — and the ones
 * already sitting in shipped apps — keep winning, whatever the source order.
 * These are defaults, not opinions: an app that overrides one is not fighting
 * us, it is doing the thing this design allows.
 *
 * Colours come from DaisyUI 5 theme variables (var(--color-*)), so the editor
 * follows whatever `data-theme` the host sets.
 */
const STYLE_ID = 'g-editor-styles';

const EDITOR_CSS = `
:where(.g-editor-root) .ProseMirror { outline: none; min-height: 120px; }
:where(.g-editor-root) .ProseMirror > :first-child { margin-top: 0; }

/* Typography */
:where(.g-editor-root) h1 { font-size: 1.875rem; font-weight: 700; margin: .5em 0; line-height: 1.2; }
:where(.g-editor-root) h2 { font-size: 1.5rem; font-weight: 700; margin: .5em 0; line-height: 1.25; }
:where(.g-editor-root) h3 { font-size: 1.25rem; font-weight: 700; margin: .5em 0; line-height: 1.3; }
:where(.g-editor-root) h4 { font-size: 1.125rem; font-weight: 600; margin: .5em 0; }
:where(.g-editor-root) h5, :where(.g-editor-root) h6 { font-size: 1rem; font-weight: 600; margin: .5em 0; }
:where(.g-editor-root) p { margin: .5em 0; }
:where(.g-editor-root) strong { font-weight: 700; }
:where(.g-editor-root) em { font-style: italic; }
:where(.g-editor-root) del, :where(.g-editor-root) s { text-decoration: line-through; opacity: .7; }

/* Lists */
:where(.g-editor-root) ul { list-style: disc; padding-left: 1.5em; margin: .5em 0; }
:where(.g-editor-root) ol { list-style: decimal; padding-left: 1.5em; margin: .5em 0; }
:where(.g-editor-root) li { margin: .25em 0; }
:where(.g-editor-root) li > p { margin: 0; }

/* Task list. Milkdown renders a checked item as a data attribute and nothing
 * else, so the box is drawn here — and the gutter it sits in is the hit target
 * <g-editor> listens on to toggle it. */
:where(.g-editor-root) li[data-item-type="task"] {
  list-style: none;
  position: relative;
  padding-left: 1.5em;
  cursor: pointer;
}
:where(.g-editor-root) li[data-item-type="task"] > * { cursor: text; }
:where(.g-editor-root) li[data-item-type="task"]::before {
  content: "\\2610\\FE0E";
  position: absolute;
  left: 0;
  opacity: .7;
}
:where(.g-editor-root) li[data-item-type="task"][data-checked="true"]::before {
  content: "\\2611\\FE0E";
  color: var(--color-primary);
  opacity: 1;
}
:where(.g-editor-root) li[data-item-type="task"][data-checked="true"] { opacity: .6; }

/* Blockquote */
:where(.g-editor-root) blockquote {
  border-left: 3px solid color-mix(in oklab, var(--color-base-content) 30%, transparent);
  padding-left: 1em;
  margin: .5em 0;
  opacity: .8;
}

/* Code */
:where(.g-editor-root) code {
  background: var(--color-base-200);
  padding: .1em .3em;
  border-radius: .25em;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: .9em;
}
:where(.g-editor-root) pre {
  background: var(--color-base-200);
  padding: .75em;
  border-radius: .5em;
  overflow-x: auto;
  margin: .5em 0;
}
:where(.g-editor-root) pre code { background: none; padding: 0; font-size: .875em; }

/* Links, rules, images */
:where(.g-editor-root) a { color: var(--color-primary); text-decoration: underline; }
:where(.g-editor-root) hr {
  border: none;
  border-top: 1px solid color-mix(in oklab, var(--color-base-content) 20%, transparent);
  margin: 1em 0;
}
:where(.g-editor-root) img { max-width: 100%; height: auto; border-radius: .375rem; }
:where(.g-editor-root) .ProseMirror-selectednode {
  outline: 2px solid var(--color-primary);
  outline-offset: 1px;
}

/* Tables. table-layout:fixed and the resize handle are load-bearing for
 * prosemirror-tables' column resizing, not decoration. */
:where(.g-editor-root) .tableWrapper { overflow-x: auto; margin: .75em 0; }
:where(.g-editor-root) table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  margin: .75em 0;
}
:where(.g-editor-root) .tableWrapper > table { margin: 0; }
:where(.g-editor-root) th, :where(.g-editor-root) td {
  border: 1px solid color-mix(in oklab, var(--color-base-content) 20%, transparent);
  padding: .375rem .5rem;
  vertical-align: top;
  box-sizing: border-box;
  position: relative;
  min-width: 3em;
}
:where(.g-editor-root) th { background: var(--color-base-200); font-weight: 600; }
:where(.g-editor-root) th > p, :where(.g-editor-root) td > p { margin: 0; }
:where(.g-editor-root) .selectedCell::after {
  content: "";
  position: absolute;
  inset: 0;
  background: color-mix(in oklab, var(--color-primary) 18%, transparent);
  pointer-events: none;
  z-index: 2;
}
:where(.g-editor-root) .column-resize-handle {
  position: absolute;
  right: -2px;
  top: 0;
  bottom: 0;
  width: 4px;
  background: var(--color-primary);
  pointer-events: none;
  z-index: 20;
}
:where(.g-editor-root) .ProseMirror.resize-cursor { cursor: col-resize; }
`;

/**
 * Inject the editor stylesheet once per document.
 *
 * Keyed on the DOM rather than a module flag: rollup emits one IIFE per
 * bundle, so gainable-editor.js and gainable.js each carry their own copy of
 * this module and a module-scoped boolean would inject once per loaded bundle.
 */
export function ensureEditorStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = EDITOR_CSS;
  document.head.appendChild(style);
}
